import React, { useRef, useState } from 'react';
import { ArrowTopRightOnSquareIcon, CheckIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/solid';

import BottomScrollListener from '@dashboard/components/BottomScrollListener';
import { Command, CommandEmpty, CommandItem, CommandList } from '@dashboard/components/ui/command';
import { Input } from '@dashboard/components/ui/input';
import { Popover, PopoverAnchor, PopoverContent } from '@dashboard/components/ui/popover';
import { Skeleton } from '@dashboard/components/ui/skeleton';
import { cn } from '@dashboard/lib/utils';

export type SearchSelectProps<T> = {
  name: string;
  inputValue: string;
  onInputChange: (value: string) => void;
  placeholder: string;
  /** Leave unset to let a `<label htmlFor={name}>` name the input. */
  ariaLabel?: string;
  disabled?: boolean;
  /** Swaps the search icon for a chevron. */
  hasSelection: boolean;
  onClear: () => void;
  onOpenChange?: (open: boolean) => void;
  /** Items as their pages were fetched; `onLoadMore` appends the next page. */
  itemPages: T[][];
  getItemKey: (item: T) => string;
  selectedKey?: string;
  onSelect: (item: T) => void;
  /** The option's text column; the check and the open-in-new-tab button are added here. */
  renderItem: (item: T) => React.ReactNode;
  getItemUrl: (item: T) => string;
  openItemLabel: string;
  isLoading: boolean;
  isError: boolean;
  errorMessage: string;
  emptyMessage: string;
  onLoadMore?: () => void;
  /**
   * An ARIA combobox: arrow keys and Enter drive the list and the highlight follows
   * them. Off, the highlight stays pinned to the selection and Enter submits the
   * surrounding form, which is how PageSelect has always behaved.
   */
  combobox?: boolean;
};

const ScrollListener = BottomScrollListener as React.ComponentType<{
  onBottom: () => void;
  isLoadingMore: boolean;
  children: React.ReactNode;
}>;

const LIST_KEYS = new Set(['ArrowDown', 'ArrowUp', 'Enter']);
const noop = () => {};
const keepFocus = (event: React.MouseEvent) => event.preventDefault();

function SearchSelect<T>({
  name,
  inputValue,
  onInputChange,
  placeholder,
  ariaLabel,
  disabled,
  hasSelection,
  onClear,
  onOpenChange,
  itemPages,
  getItemKey,
  selectedKey,
  onSelect,
  renderItem,
  getItemUrl,
  openItemLabel,
  isLoading,
  isError,
  errorMessage,
  emptyMessage,
  onLoadMore = noop,
  combobox = false,
}: SearchSelectProps<T>) {
  const inputRef = useRef<HTMLInputElement>(null);
  const commandRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const open = isOpen && !disabled;

  const setOpen = (next: boolean) => {
    setIsOpen(next);
    onOpenChange?.(next);
  };

  const handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onClear();
    inputRef.current?.focus();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!LIST_KEYS.has(event.key) || event.nativeEvent.isComposing) return;

    if (!open) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setOpen(true);
      }
      return;
    }

    event.preventDefault();
    // cmdk only handles these keys on its own root, and focus stays in this input.
    commandRef.current?.dispatchEvent(
      new KeyboardEvent('keydown', { key: event.key, bubbles: true, cancelable: true }),
    );
  };

  const comboboxProps = combobox
    ? {
        role: 'combobox',
        'aria-expanded': open,
        'aria-autocomplete': 'list' as const,
        'aria-haspopup': 'listbox' as const,
      }
    : {};

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div className="relative">
          <Input
            ref={inputRef}
            id={name}
            name={name}
            value={inputValue}
            placeholder={placeholder}
            autoComplete="off"
            aria-label={ariaLabel}
            {...comboboxProps}
            disabled={disabled}
            onChange={event => {
              onInputChange(event.target.value);
              if (!open) setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            // The input keeps focus after a pick, so a click has to reopen the list too.
            onClick={combobox ? () => setOpen(true) : undefined}
            onKeyDown={combobox ? handleKeyDown : undefined}
            className="pr-16"
          />
          <div className="absolute inset-y-0 right-2 flex items-center gap-1 pointer-events-none">
            {inputValue.length > 0 && !disabled && (
              <button
                type="button"
                onMouseDown={handleClear}
                className="text-muted-foreground transition-colors hover:text-foreground pointer-events-auto"
                aria-label="Clear selection"
              >
                <XMarkIcon className="size-4" />
              </button>
            )}
            {hasSelection ? (
              <ChevronDownIcon className="size-4 text-muted-foreground" />
            ) : (
              <MagnifyingGlassIcon className="size-4 text-muted-foreground/60" />
            )}
          </div>
        </div>
      </PopoverAnchor>

      <PopoverContent
        // Inside a modal dialog the list portals into the dialog: the dialog's scroll
        // lock swallows wheel events on anything left on document.body.
        container={inputRef.current?.closest<HTMLElement>('[role="dialog"]') ?? undefined}
        className="p-0 w-[var(--radix-popover-trigger-width)]"
        align="start"
        sideOffset={4}
        onOpenAutoFocus={event => event.preventDefault()}
        onInteractOutside={event => {
          if (inputRef.current && inputRef.current.contains(event.target as Node)) {
            event.preventDefault();
          }
        }}
      >
        <Command
          ref={commandRef}
          shouldFilter={false}
          value={combobox ? undefined : selectedKey}
          defaultValue={combobox ? selectedKey : undefined}
        >
          <CommandList className="max-h-72 p-1 scrollbar-hide">
            {isError && <CommandEmpty>{errorMessage}</CommandEmpty>}
            {!isError && !isLoading && !itemPages[0]?.length && (
              <CommandEmpty>{emptyMessage}</CommandEmpty>
            )}

            <ScrollListener onBottom={onLoadMore} isLoadingMore={isLoading}>
              {itemPages.map(page =>
                page.map(item => {
                  const key = getItemKey(item);

                  return (
                    <CommandItem
                      key={key}
                      value={key}
                      onMouseDown={combobox ? keepFocus : undefined}
                      onSelect={() => {
                        onSelect(item);
                        setOpen(false);
                      }}
                      className="flex items-center gap-2"
                    >
                      <div className="min-w-0 flex-1">{renderItem(item)}</div>

                      <div className="ml-auto flex items-center gap-1.5">
                        <CheckIcon
                          className={cn(
                            'size-4 shrink-0 text-foreground',
                            key === selectedKey ? 'opacity-100' : 'opacity-0',
                          )}
                        />
                        <button
                          type="button"
                          onMouseDown={event => event.preventDefault()}
                          onClick={event => {
                            event.stopPropagation();
                            window.open(getItemUrl(item), '_blank');
                          }}
                          className="text-muted-foreground transition-colors hover:text-foreground"
                          aria-label={openItemLabel}
                        >
                          <ArrowTopRightOnSquareIcon className="size-4" />
                        </button>
                      </div>
                    </CommandItem>
                  );
                }),
              )}
            </ScrollListener>

            {isLoading &&
              Array.from({ length: 5 }).map((_, index) => (
                <div
                  key={index}
                  aria-hidden="true"
                  className="flex items-center gap-2 rounded-md px-1.5 py-1"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex h-4 items-center">
                      <Skeleton className="h-3 w-12" />
                    </div>
                    <div className="flex h-5 items-center">
                      <Skeleton className="h-3.5 w-40" />
                    </div>
                  </div>
                  <Skeleton className="size-4 shrink-0 rounded-md" />
                </div>
              ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default SearchSelect;
